<template>
  <v-dialog v-model="value" max-width="600px" persistent @keydown.esc="handleEscape">
    <v-card :loading="editLoading">
      <v-card-title>
        <span class="headline font-weight-thin">Edit SA {{ serviceArea.id }}: {{ serviceArea.name }}</span>
      </v-card-title>
      <v-card-text>
        <v-form v-model="valid" ref="form" :disabled="editLoading" lazy-validation>
          <user-picker
            attributeName="accountManager"
            :outlined="true"
            :is-required="true"
            field-name="Account Manager"
            label="Account Manager"
            v-model="serviceArea.accountManager"
            class="mt-3"
            data-cy="account-manager-picker"
            roleFilter="ochinAccountManager"
          ></user-picker>

          <v-alert type="info" text dense class="mt-3">
            Only OCHIN users with the Account Manager role appear in this list. If someone is missing from the list,
            they either haven't been assigned the Account Manager role within Continuum or they haven't yet been
            imported into the application. If you don't see a particular Account Manager in this list, contact Help Desk
            to request access.
          </v-alert>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="close" data-cy="cancel-button" outlined small :disabled="isSaving || editLoading"
              >Cancel</v-btn
            >
            <v-btn
              @click="save"
              :disabled="!valid || isSaving || editLoading"
              :loading="isSaving"
              color="primary"
              data-cy="save-button"
              outlined
              small
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import UserPicker from "@/components/userPicker";

export default {
  components: {
    UserPicker,
  },
  computed: {
    ...mapState({
      editLoading: state => state.dashboard.serviceArea.editLoading,
    }),
    packIdRouteParam() {
      return parseInt(this.$route.params.packId, 10);
    },
  },
  data() {
    return {
      isSaving: false,
      valid: true,
      serviceArea: {},
    };
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    handleEscape() {
      // Prevent closing during save operation or while loading
      if (!this.isSaving && !this.editLoading) {
        this.close();
      }
    },
    async save() {
      // Validate form before saving
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      try {
        await this.updateServiceArea({
          packId: this.packIdRouteParam,
          serviceArea: this.serviceArea,
        });
        this.$notifySuccess({ message: "Account Manager has been updated" });
        this.close();
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Unable to update Service Area" });
      }
      this.isSaving = false;
    },
    ...mapActions({
      loadEdit: "dashboard/serviceArea/loadEdit",
      updateServiceArea: "dashboard/serviceArea/updateServiceArea",
    }),
  },
  props: {
    value: { required: true, type: Boolean },
    serviceAreaId: { required: false, type: Number, default: null },
  },
  watch: {
    async value(val) {
      if (!val) {
        // Reset validation when closing the modal
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
        // Clear local state when closing
        this.serviceArea = {};
        return;
      }

      if (this.serviceAreaId) {
        try {
          await this.loadEdit(this.serviceAreaId);
          // Copy Vuex state into local component state for editing
          this.serviceArea = { ...this.$store.state.dashboard.serviceArea.edit };
        } catch (e) {
          console.error(e);
          this.$notifyError({ message: "Unable to load Service Area details" });
          this.close();
          return;
        }
      }

      // Reset validation when opening the modal
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>
